import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { DEMO_CTA_HREF } from '@/config';
import { useReveal } from '@/hooks/useReveal';
import { useMedia } from '@/hooks/useMedia';
import { logContent } from './guide/beats';
import { useSectionEnter } from './guide/useSectionEnter';
import { SessionLedger } from './SessionLedger';
import { IntegrationLogos } from './IntegrationLogos';
import { FunnelSection } from './FunnelSection';
import { OneBrainSection } from './OneBrainSection';
import { StatementRail } from './StatementRail';
import { InfrastructureSection } from './InfrastructureSection';
import { HowItWorks } from './HowItWorks';
import { GuideStage, useStage } from './guide/stage';
import './platform-home.css';

const questions = [
  [
    'What is Foxet?',
    'Intelligence infrastructure for your Product & GTM. Product expertise for customer conversations, guided experiences and team decisions.',
  ],
  [
    'How does Foxet learn our product?',
    'Product exploration, captured workflows, documentation and your team’s expertise. You review before publishing.',
  ],
  [
    'Is Foxet only for demonstrations?',
    'Pre-sales and demonstrations are live today. Onboarding and support draw on the same brain and are in direction; scope is agreed with your team.',
  ],
  [
    'What stays in our team’s control?',
    'The knowledge, approved workflows, the never-say list, review of outbound follow-ups, and the ability to step into a live session.',
  ],
  [
    'How can we evaluate Foxet?',
    'Try Foxet’s own product expert, or talk to our team about a focused evaluation on your product.',
  ],
];

// Four facts, limited to what the truth inventory supports; short, one quiet line (owner, 2026-10-16).
const facts = [
  ['Access', 'tenant-scoped, role-based'],
  ['Captures', 'PII redacted'],
  ['Outbound', 'held for your review'],
  ['Documentation', 'security notes on request'],
];

// B18 (content): two stacked rows (owner, 2026-10-16: side by side read as cluttered), copy left, document right. The
// documents come alive on entry — the digest's three lines reveal one by one (fade + 6px rise, 220ms apart), then the
// Brief's four. Mr. Clippy half-lids at the Product digest (above), looks at the Brief (below), and when "Next step ·
// 30-min evaluation, owner: AE" reveals he does the wind-up pop and goes wide-eyed for 280ms. Reduced motion: no reveal.
function ProductGTMStory() {
  const section = useRef<HTMLElement>(null);
  const brief = useRef<HTMLDivElement>(null);
  const reduced = useMedia('(prefers-reduced-motion: reduce)');
  const stage = useStage();
  const [shown, setShown] = useState(reduced ? 7 : 0); // lines revealed: 0–3 the digest, 3–7 the brief
  useEffect(() => { if (reduced) setShown(7); }, [reduced]);
  useSectionEnter(section, '.fx-two', () => {
    logContent('C18', 'digest lines, then brief lines reveal');
    if (reduced) { setShown(7); return; }
    stage?.react('.fx-two', 'clippy', { eyes: { mode: 'half-lid', target: { x: -1, y: 0.1 }, key: 'digest' } });
    for (let i = 1; i <= 7; i += 1) {
      window.setTimeout(() => {
        setShown(i);
        if (i === 4) stage?.react('.fx-two', 'clippy', { eyes: { mode: 'look', target: { x: 0, y: 0 }, key: 'brief' }, lookAt: brief.current });
        if (i === 7) { stage?.react('.fx-two', 'clippy', { pop: true, eyes: { mode: 'wide', target: { x: -0.6, y: 0.3 }, key: 'next-step' } }); window.setTimeout(() => stage?.react('.fx-two', 'clippy', { lookAt: brief.current }), 280); }
      }, i * 220);
    }
  });
  const cls = (i: number) => (i < shown ? 'in' : undefined);
  return (
    <section className={`fx-two${reduced ? '' : ' fx-two-live'}`} ref={section}>
      <div className="container">
        <div className="fx-two-row fx-rv fx-two-product">
          <div className="fx-col">
            <p className="fx-eyebrow">For Product</p>
            <h2>Hear what buyers actually say.</h2>
            <p>Every session is read for you. Questions, objections and stalled steps land on your desk, in the buyer’s own words.</p>
            <ul className="fx-points">
              <li>Signals, clustered into what to build next</li>
              <li>Flows that stall, with the step they stall on</li>
              <li>Language and feature requests, counted across sessions</li>
            </ul>
            <Link className="fx-more" to="/product">
              Explore for Product →
            </Link>
          </div>
          <div className="fx-doc">
            <div className="fx-doc-h">
              <span>Weekly digest</span>
            </div>
            <ul>
              <li className={cls(0)}>
                <b>“Can we manage approvals across regions?”</b> asked again this
                week
              </li>
              <li className={cls(1)}>
                <b>Export report</b> flow stalled at step 4
              </li>
              <li className={cls(2)}>
                <b>Hindi</b> requested; held for the rest of each call
              </li>
            </ul>
            <div className="fx-doc-img">
              <img
                src="/media/placeholder-session/insights-sample.jpg"
                alt="The Signals screen in the Foxet dashboard: what buyers raised in conversations, clustered into what to do"
                width="1600"
                height="900"
                loading="lazy"
              />
            </div>
          </div>
        </div>
        <div className="fx-two-row fx-rv fx-two-gtm">
          <div className="fx-col">
            <p className="fx-eyebrow">For GTM</p>
            <h2>Every session ends with a next step.</h2>
            <p>A brief, a drafted follow-up and a CRM row from every conversation. Nothing goes out before your team has reviewed it.</p>
            <ul className="fx-points">
              <li>A brief: role, process today, open questions, next step</li>
              <li>Follow-ups drafted, held until you approve them</li>
              <li>A CRM row in Salesforce, HubSpot or Zoho</li>
            </ul>
            <Link className="fx-more" to="/pre-sales">
              Explore for GTM →
            </Link>
          </div>
          <div className="fx-doc" ref={brief}>
            <div className="fx-doc-h">
              <span>Brief · after the session above</span>
            </div>
            <ul>
              <li className={cls(3)}>
                <b>Role</b> Head of RevOps, 40-person sales org
              </li>
              <li className={cls(4)}>
                <b>Process today</b> inbound to SDR queue, 2-day lag
              </li>
              <li className={cls(5)}>
                <b>Open</b> regional approvals; export
              </li>
              <li className={cls(6)}>
                <b>Next step</b> 30-min evaluation, owner: AE
              </li>
            </ul>
            <div className="fx-doc-img">
              <img
                src="/media/placeholder-session/brief-sample.jpg"
                alt="A brief in the Foxet dashboard with its draft follow-up email, sample data"
                width="1600"
                height="900"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// B19 (content): the four facts reveal in sequence (180ms apart) on entry; Mr. Clippy neutral, one slow blink on
// "Outbound · held for review".
function EnterpriseStory() {
  const section = useRef<HTMLElement>(null);
  const reduced = useMedia('(prefers-reduced-motion: reduce)');
  const stage = useStage();
  const [shown, setShown] = useState(reduced ? 4 : 0);
  useEffect(() => { if (reduced) setShown(4); }, [reduced]);
  useSectionEnter(section, '.fx-ent', () => {
    logContent('C19', 'the four facts reveal in sequence');
    if (reduced) { setShown(4); return; }
    stage?.react('.fx-ent', 'clippy', { eyes: { mode: 'neutral', key: 'ent' } });
    for (let i = 1; i <= 4; i += 1) window.setTimeout(() => { setShown(i); if (i === 3) stage?.react('.fx-ent', 'clippy', { blink: true }); }, i * 180);
  });
  return (
    <section className={`fx-ent fx-rv${reduced ? '' : ' fx-ent-live'}`} ref={section}>
      <div className="container">
        <div className="fx-ent-box">
          <div className="fx-ent-head">
            <p className="fx-eyebrow">Enterprise, on your terms</p>
            <h2>Your knowledge, your oversight, your rollout.</h2>
            <p>Scope the products, the sources and the boundaries. Review before anything goes out.</p>
          </div>
          <IntegrationLogos />
          <ul className="fx-facts">
            {facts.map(([label, text], i) => (
              <li key={label} className={i < shown ? 'in' : undefined}>
                <b>{label}</b> {text}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Questions() {
  const stage = useStage();
  // FAQ open: Mr. Clippy looks at the answer (B20).
  const onToggle = (e: React.SyntheticEvent<HTMLDetailsElement>) => {
    if (!stage) return;
    const open = Array.from(e.currentTarget.parentElement?.querySelectorAll<HTMLDetailsElement>('details[open]') ?? []);
    if (open.length) logContent('C20', 'the answer reveals on open');
    // B20: eyes to the open answer, back to neutral when it closes
    stage.react('.fx-faq', 'clippy', { lookAt: open.length ? open[open.length - 1].querySelector('p') : null, user: true });
  };
  return (
    <section className="fx-faq fx-rv" id="faq">
      <div className="container">
        <div>
          <p className="fx-eyebrow">Questions, answered</p>
          <h2>Let’s make it clear.</h2>
          <a className="fx-more" href={DEMO_CTA_HREF}>
            Ask Foxet about Foxet →
          </a>
        </div>
        <div>
          {questions.map(([question, answer]) => (
            <details key={question} onToggle={onToggle}>
              <summary>{question}</summary>
              <p>{answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function Invitation() {
  const stage = useStage();
  const section = useRef<HTMLElement>(null);
  useSectionEnter(section, '.fx-invite', () => logContent('C21', 'the invitation enters; eyes wide to the CTA, the pop, the nod'));
  // Hover on the ember CTA: Mr. Clippy looks at the cursor (while this section owns his eyes).
  const look = (e: React.MouseEvent) => {
    if (!stage) return;
    const home = (e.currentTarget as HTMLElement).closest('.fx-home')?.getBoundingClientRect();
    const p = home ? { x: e.clientX - home.left, y: e.clientY - home.top } : null;
    stage.react('.fx-invite', 'clippy', { lookAt: p, user: true });
  };
  const unlook = () => {
    stage?.react('.fx-invite', 'clippy', { lookAt: null, user: true });
  };
  return (
    <section className="fx-invite" ref={section}>
      <div className="fx-invite-wash" aria-hidden="true" />
      <img className="fx-invite-mark" src="/fox-icon.svg" alt="" aria-hidden="true" />
      <div className="container">
        <div className="fx-rv">
          <h2>Meet your next product expert.</h2>
          <p>Start with Foxet. Imagine it with your product.</p>
        </div>
        <div className="fx-rv">
          <div className="button-row">
            <Link className="button button-secondary" to="/contact">
              Talk to Sales <ArrowRight size={16} aria-hidden="true" />
            </Link>
            <a className="button button-primary" href={DEMO_CTA_HREF} onMouseMove={look} onMouseLeave={unlook}>
              See Foxet in action <ArrowRight size={16} aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * The numbers strip, reinstated under the hero rail (it was removed on 2026-10-15 in ad815552,
 * where the live-session widget and the statement rail took its slot; the version removed carried
 * owner-supplied impact figures — 16% / 43% / 34% — that nothing on the running system backs).
 *
 * EVERY figure here is measured on PROD, not supplied. Snapshot 2026-10-22; re-verify before a
 * launch, the counts only grow:
 *   892   SELECT count(*), count(DISTINCT product_id) FROM step_captures            → 892 | 11
 *   21018 SELECT count(*), count(DISTINCT (pkb_id, flow_id)) FROM flow_validation_runs → 21018 | 63
 *   7     IntegrationType, packages/shared/src/types/integrations.ts — SALESFORCE, HUBSPOT, ZOHO,
 *         DYNAMICS, SLACK, TEAMS, WEBHOOK; one adapter per name under
 *         apps/api/src/modules/analytics/integrations/, each with a real push/notify export.
 *         Excluded on purpose: SENDGRID (the mail vendor, not a destination), CONFLUENCE
 *         (read-only import, nothing is reported INTO it), and JIRA/LINEAR — this page marks
 *         those two as direction, not live (the Infrastructure chips carry `dir`), so counting
 *         them here would overclaim against the page's own vocabulary.
 *
 * Deliberately NOT here:
 * - Session, product or tenant counts. Every tenant on the platform is a test tenant, so a volume
 *   number would read as customer traction that does not exist.
 * - The spoken-language count. It is TRUE (5 — SPOKEN_LANGUAGES in
 *   packages/shared/src/types/spoken-language.ts:20, mirrored by SUPPORTED in
 *   services/pipecat-agent/speech_policy.py:26) and it was the natural fourth tile, but this page's
 *   own gate suite bars that claim: docs/review/landing-v3-evidence/gates.mjs:8 lists '5 languages',
 *   'five languages' and 'five spoken' in NEVER. The rail above already says "Multi-lingual".
 *   Reinstate only if the owner lifts that gate.
 */
// Owner-supplied marketing figures (first put on this page in 47665e3d, 2026-10-15; brought
// back 2026-10-22 at the owner's request over the platform counts an earlier pass used).
// These are positioning claims, not platform measurements: no baseline, sample size or
// measurement definition has been independently verified, and the owner has chosen to run
// them knowing that. Do not relabel them "measured".
// Details are held to one line at 1440 — the band breaks if they wrap.
const metrics = [
  ['16%', 'Shorter sales cycle', 'Buyers see the product on day one.'],
  ['43%', 'Deeper engagement', 'A live walk holds attention a video can’t.'],
  ['34%', 'Higher conversion', 'Every session: a brief and a next step.'],
] as const;

function PlatformMetrics() {
  return (
    <section className="fx-metrics fx-rv" aria-label="Foxet, in numbers">
      <div className="container">
        <dl>
          {metrics.map(([value, label, detail]) => (
            // dt before dd as the dl grammar requires; the cell grid places the numeral first.
            <div key={label}>
              <dt>{label}</dt>
              <dd className="fx-metric-v">{value}</dd>
              <dd className="fx-metric-d">{detail}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

export function PlatformHome() {
  const root = useRef<HTMLDivElement>(null);
  useReveal(root);
  return (
    <div className="fx-home" ref={root}>
      <PlatformMetrics />
      <GuideStage>
        <SessionLedger />
        <FunnelSection />
        <OneBrainSection />
        <StatementRail />
        <InfrastructureSection />
        <HowItWorks />
        <ProductGTMStory />
        <EnterpriseStory />
        <Questions />
        <Invitation />
      </GuideStage>
    </div>
  );
}
